import {
    Typography,
    TextField,
    Grid,
    FormHelperText,
} from '@mui/material';
import { FormikProps } from 'formik';
import { CustomerTypes } from '../../../../../types';

interface Props {
    formik: FormikProps<CustomerTypes.OnboardingForm>;
    /**
     * Both retention fields are v2 catalog settings, so the whole tab is
     * read-only. Defaults to disabled so a caller that forgets the prop
     * cannot reopen a legacy write path.
     */
    disabled?: boolean;
}

const parseOptionalNumber = (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

export const DataRetentionSettingsTab: React.FC<Props> = ({ formik, disabled = true }) => {
    const { getFieldProps, touched, errors, setFieldValue } = formik;

    return (
        <>
            <Typography fontWeight={500} fontSize={18} mt={2}>
                Data Retention
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Customize how long alerts and reports are stored by setting a retention policy in days. This feature helps manage storage efficiently while ensuring data is retained in compliance with operational and regulatory needs.
            </Typography>

            <Grid container spacing={2} mt={0.5}>
                <Grid item xl={4} md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        type="number"
                        label="Reports Retention (days)"
                        placeholder="Reports"
                        {...getFieldProps('reportsRetention')}
                        onChange={(e) => setFieldValue('reportsRetention', parseOptionalNumber(e.target.value))}
                        disabled={disabled}
                        InputProps={{ readOnly: disabled }}
                        error={Boolean(touched.reportsRetention && errors.reportsRetention)}
                        helperText={touched.reportsRetention && errors.reportsRetention}
                    />
                    <FormHelperText sx={{ mt: 0.5 }}>
                        Number of days to retain report data before automatic deletion. Range: 1-365 days.
                    </FormHelperText>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                    <TextField
                        fullWidth
                        size="medium"
                        type="number"
                        label="Alerts Retention (days)"
                        placeholder="Alerts"
                        {...getFieldProps('alertsRetention')}
                        onChange={(e) => setFieldValue('alertsRetention', parseOptionalNumber(e.target.value))}
                        disabled={disabled}
                        InputProps={{ readOnly: disabled }}
                        error={Boolean(touched.alertsRetention && errors.alertsRetention)}
                        helperText={touched.alertsRetention && errors.alertsRetention}
                    />
                    <FormHelperText sx={{ mt: 0.5 }}>
                        Number of days to retain alert data before automatic deletion. Range: 1-365 days.
                    </FormHelperText>
                </Grid>
            </Grid>
        </>
    );
};
